
import React from 'react';
import { motion } from 'framer-motion';
import { Copy, Bolt, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent } from '@/components/ui/card';

interface PromptDisplayProps {
  prompts: string[];
  isGenerating: boolean;
  onRegeneratePrompt: (index: number) => void;
  onCopyAllPrompts: () => void;
}

const PromptDisplay = ({ 
  prompts, 
  isGenerating, 
  onRegeneratePrompt,
  onCopyAllPrompts 
}: PromptDisplayProps) => {
  const { toast } = useToast();
  
  const copyPrompt = (prompt: string) => {
    navigator.clipboard.writeText(prompt);
    toast({
      title: "Copied to clipboard",
      description: "Prompt has been copied to your clipboard",
    });
  };

  const formatPrompt = (text: string) => {
    const lines = text.split('\n');
    return lines.map((line, i) => {
      if (line.startsWith('# ')) {
        return (
          <h4 key={i} className="font-bold text-purple-400 text-lg mb-2">
            {line.replace('# ', '')}
          </h4>
        );
      } else if (line.startsWith('## ')) {
        return (
          <h5 key={i} className="font-semibold text-white/90 text-base mb-2">
            {line.replace('## ', '')}
          </h5>
        );
      } else if (line.trim() === '') {
        return <br key={i} />;
      } else {
        return <p key={i} className="mb-2 text-white/90">{line}</p>;
      }
    });
  };

  return (
    <div className="glass p-6 rounded-xl bg-gray-800/30 backdrop-blur border border-white/5 h-full">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center">
          <span className="bg-gradient-to-r from-indigo-500 to-purple-500 w-1.5 h-5 rounded mr-2"></span>
          <h2 className="text-xl font-semibold font-display">Generated Prompts</h2>
        </div>
        
        <Button 
          variant="outline" 
          onClick={onCopyAllPrompts}
          disabled={!prompts.length}
          className="text-xs bg-gray-800/50 border-white/10 hover:bg-secondary"
        >
          <Copy className="h-3 w-3 mr-1" />
          Copy All
        </Button>
      </div>
      
      {isGenerating && prompts.length === 0 ? (
        <div className="flex justify-center items-center py-20">
          <div className="flex flex-col items-center">
            <div className="h-10 w-10 rounded-full border-4 border-t-transparent border-purple-500 animate-spin mb-4"></div>
            <p className="text-muted-foreground">Engineering your perfect prompts...</p>
          </div>
        </div>
      ) : prompts.length > 0 ? (
        <div className="space-y-6 max-h-[calc(100vh-220px)] overflow-y-auto pr-2">
          {prompts.map((prompt, index) => (
            <Card key={index}
              className="border-purple-500/20 bg-gray-800/30 overflow-hidden transition-all duration-200 hover:border-purple-500/40"
            >
              <CardContent className="p-5">
                <div className="flex justify-between items-start mb-3">
                  <div className="text-xs font-medium bg-indigo-600/20 px-2 py-1 rounded text-indigo-300">
                    Prompt {index + 1}
                  </div>
                  
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onRegeneratePrompt(index)}
                      disabled={isGenerating}
                      className="h-8 px-2 bg-gray-800/70 border-white/10 hover:bg-secondary"
                    >
                      {isGenerating ? (
                        <div className="h-3 w-3 rounded-full border-2 border-t-transparent border-white animate-spin"></div>
                      ) : (
                        <Bolt className="h-3 w-3" />
                      )}
                      <span className="text-xs ml-1">Enhance</span>
                    </Button>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyPrompt(prompt)}
                      className="h-8 px-2 bg-gray-800/70 border-white/10 hover:bg-secondary"
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
                
                <motion.div 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="text-sm text-white/90 whitespace-pre-line font-mono"
                >
                  {formatPrompt(prompt)}
                </motion.div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-16">
          <MessageSquare className="h-12 w-12 text-indigo-500 mx-auto mb-4 opacity-50" />
          <h3 className="text-xl font-semibold mb-2 font-display">Creating Your Prompts</h3>
          <p className="text-muted-foreground max-w-md mx-auto">
            We're engineering the perfect AI prompts based on your description. 
            These will help you code faster with your favorite AI coding assistant.
          </p>
        </div>
      )}
    </div>
  );
};

export default PromptDisplay;
